import { useEffect, useRef, type ComponentType } from "react";
import { useLocation } from "react-router";
import { Stack, Text } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import type { ReadHomeBlock } from "@ferrivia/api-contract";
import { useAppContext } from "../../app/useAppContext";
import { ProfileMenu } from "../../components/ProfileMenu";
import { useHomeSurface } from "../../hooks/useHomeSurface";
import { useRefetchOnOverlayClose } from "../../hooks/useRefetchOnOverlayClose";
import { ChatUnreadBlock } from "./blocks/ChatUnreadBlock";
import { CoachingNextStepBlock } from "./blocks/CoachingNextStepBlock";
import { PathChooserBlock } from "./blocks/PathChooserBlock";
import { PlanBlock } from "./blocks/PlanBlock";
import { SummaryReadyBlock } from "./blocks/SummaryReadyBlock";
import s from "./HomeScreen.module.css";

// A registry, zero conditionals on stage or facts — a new block type is a new
// row here, never new plumbing. Unknown block type renders nothing: an old
// client must survive a new server (see readHomeBlockSchema). Cast to a common
// shape since each renderer only accepts its own block variant.
const RENDERERS: Record<string, ComponentType<Record<string, unknown>>> = {
  chatUnread: ChatUnreadBlock as ComponentType<Record<string, unknown>>,
  coachingNextStep: CoachingNextStepBlock as ComponentType<Record<string, unknown>>,
  pathChooser: PathChooserBlock as ComponentType<Record<string, unknown>>,
  plan: PlanBlock as ComponentType<Record<string, unknown>>,
  summaryReady: SummaryReadyBlock as ComponentType<Record<string, unknown>>,
};

// Which blocks render as a pinned notice above the column rather than as a
// card in it. Keyed on block TYPE, not on `slot`: "pinned" describes sort
// order on the wire; being a notification is a property of the block itself.
const NOTIFICATION_TYPES = new Set(["chatUnread", "summaryReady"]);

// Home is a landing page for notifications while its content is redesigned.
// Everything below the header row is server-resolved (journey/rules.ts), so a
// new block ships as a rule plus a renderer.
export function HomeScreen() {
  const { t } = useTranslation("client");
  const { user } = useAppContext();
  const surface = useHomeSurface();
  const { refetch } = surface;
  const { pathname } = useLocation();

  // Every tab panel stays mounted (app/TabPanels.tsx) and useHomeSurface
  // fetches on mount without polling, so home would otherwise still be
  // showing "2 uutta viestiä" after the client read the thread and came back.
  // The gap this leaves: a message arriving while the client sits on home
  // lights the tab badge (which does poll) without adding the notice until
  // they leave and come back.
  const isActive = pathname === "/";
  const wasActiveRef = useRef(isActive);
  useEffect(() => {
    if (isActive && !wasActiveRef.current) refetch();
    wasActiveRef.current = isActive;
  }, [isActive, refetch]);

  useRefetchOnOverlayClose(refetch);

  const blocks: ReadHomeBlock[] = surface.state.status === "success" ? surface.state.surface.blocks : [];

  // Partitioned, not filtered twice, so both regions keep the server's order.
  const notifications = blocks.filter((block) => NOTIFICATION_TYPES.has(block.type));
  const cards = blocks.filter((block) => !NOTIFICATION_TYPES.has(block.type));

  const renderBlock = (block: ReadHomeBlock, index: number) => {
    const Renderer = RENDERERS[block.type];
    return Renderer ? <Renderer key={`${block.type}-${index}`} {...block} refetch={surface.refetch} /> : null;
  };

  // A name the client chose wins; the sign-in identity's given name (eID in
  // Finland, typed at onboarding in the US) is the fallback.
  const name = user.preferredName?.trim() || user.givenName?.trim() || null;
  // "Takaisin" on every visit but the day the account was made. Without a
  // createdAt (an older api) it stays the plain welcome.
  const returning = user.createdAt !== undefined && new Date(user.createdAt).toDateString() !== new Date().toDateString();
  const greeting = returning
    ? name
      ? t("home.welcomeBackNamed", "Tervetuloa takaisin, {{name}}", { name })
      : t("home.welcomeBack", "Tervetuloa takaisin!")
    : name
      ? t("home.welcomeNamed", "Tervetuloa, {{name}}", { name })
      : t("home.welcome", "Tervetuloa!");

  return (
    <>
      <div className={s.header}>
        <Text as="h1" size="xl" weight="bold" className={s.title}>
          {greeting}
        </Text>
        <ProfileMenu className={s.profile} />
      </div>
      {notifications.length > 0 ? (
        <Stack gap="2" className={s.pinned}>
          {notifications.map(renderBlock)}
        </Stack>
      ) : null}
      {cards.length > 0 ? (
        <Stack gap="3" className={s.blocks}>
          {cards.map(renderBlock)}
        </Stack>
      ) : null}
    </>
  );
}
